import { useEffect, useRef } from "react";
import { trackSectionView, setLastSeenSection, isTrackingAllowed } from "@/lib/analytics";

interface SectionConfig {
  id: string;
  name: string;
}

const SECTIONS: SectionConfig[] = [
  { id: "hero", name: "Hero" },
  { id: "quickfacts", name: "QuickFacts" },
  { id: "fuhrpark", name: "Fuhrpark" },
  { id: "preise", name: "Preise" },
  { id: "technische-daten", name: "Technische Daten" },
  { id: "anwendungsfaelle", name: "Anwendungsfälle" },
  { id: "vertrauen", name: "Vertrauen" },
  { id: "werte", name: "Werte" },
  { id: "lokal", name: "Lokal" },
  { id: "faq", name: "FAQ" },
  { id: "anfrage", name: "Kontaktformular" },
];

export const useSectionTracking = () => {
  const trackedSections = useRef<Set<string>>(new Set());
  const observedIds = useRef<Set<string>>(new Set());

  useEffect(() => {
    if (!isTrackingAllowed()) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const id = entry.target.id;
            const section = SECTIONS.find((s) => s.id === id);
            if (!section) return;

            setLastSeenSection(id);

            if (!trackedSections.current.has(id)) {
              trackedSections.current.add(id);
              trackSectionView(id, section.name);
            }
          }
        });
      },
      { threshold: 0.1 }
    );

    // Track hero immediately if visible on load
    const heroSection = SECTIONS.find(s => s.id === "hero");
    if (heroSection && !trackedSections.current.has("hero")) {
      trackedSections.current.add("hero");
      setLastSeenSection("hero");
      trackSectionView("hero", heroSection.name);
    }

    // Continuously look for new sections as lazy-loaded content appears
    const tryObserve = () => {
      SECTIONS.forEach(({ id }) => {
        if (observedIds.current.has(id)) return;
        const el = document.getElementById(id);
        if (el) {
          observer.observe(el);
          observedIds.current.add(id);
        }
      });
    };

    // Initial attempt
    tryObserve();

    // Use MutationObserver to detect when lazy-loaded sections appear
    const mutationObserver = new MutationObserver(() => {
      if (observedIds.current.size < SECTIONS.length) {
        tryObserve();
      } else {
        mutationObserver.disconnect();
      }
    });

    mutationObserver.observe(document.body, {
      childList: true,
      subtree: true,
    });

    return () => {
      observer.disconnect();
      mutationObserver.disconnect();
    };
  }, []);
};
